import { useMutation } from '@tanstack/react-query';
import type { Product as ProductDTO } from '@setvi/shared/interfaces';
import {
  fetchProductsPricing,
  PricingType
} from '@setvi/shared/services/react-query';
import {
  addProductsOptimistically,
  extractItems,
  buildQuoteRequestBody,
  updateItemsWithPricing
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';
import { useQueuedUpdateMutation } from './use-queued-update-mutation';

interface ProductWithFreeHand extends ProductDTO {
  isFreeHand?: boolean;
}

interface AddProductsVariables {
  products: ProductDTO[];
  parentId?: string | null;
}

/**
 * Add products to quote with pricing fetch and optimistic updates
 * 1. Adds products optimistically to quote structure (with loading state)
 * 2. Fetches pricing from /pricing/get endpoint
 * 3. Updates products with pricing data
 * 4. Saves complete quote via API
 */
export const useAddProducts = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const {
    mutateAsync: updateQuote,
    hasQueuedMutations,
    setOnQueueEmpty
  } = useQueuedUpdateMutation();

  return useMutation({
    mutationFn: async ({ products }: AddProductsVariables) => {
      await cache.cancel();

      // Get the optimistically updated quote from cache (onMutate already added products)
      const updatedQuote = cache.getQuote();
      if (!updatedQuote) {
        throw new Error('Quote not found in cache');
      }

      // Filter out freehand products - they don't need pricing
      const productsNeedingPricing = products.filter(
        product => !(product as ProductWithFreeHand).isFreeHand
      );

      // Fetch pricing for non-freehand products
      if (productsNeedingPricing.length > 0) {
        try {
          const accountNumber = updatedQuote.account?.accountNumber;
          const segmentId = updatedQuote.segment?._id;

          const pricingResponse = await fetchProductsPricing({
            accountNumber: accountNumber ?? segmentId,
            products: productsNeedingPricing.map(product => ({
              sku: product.sku,
              quantity: 1
            })),
            type: PricingType.Quote
          });

          // Update items with pricing data
          if (pricingResponse.pricing?.length > 0) {
            updatedQuote.items = updateItemsWithPricing(
              updatedQuote.items,
              pricingResponse.pricing
            );

            // Update cache with pricing data
            cache.setQuote(() => updatedQuote);
          }
        } catch {
          // Continue with save - products will retain isPricingFetching flag
          // and users can manually refresh pricing later
        }
      }

      const items = extractItems(updatedQuote.items);

      return updateQuote({
        quoteId,
        body: buildQuoteRequestBody(updatedQuote, items)
      });
    },

    onMutate: async ({ products, parentId }) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();

      // Optimistically add products with loading state
      cache.setQuote(old => addProductsOptimistically(old, products, parentId));

      return { previousQuote };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
    },

    onSuccess: () => {
      // Skip refetch if there are queued mutations to preserve optimistic updates
      if (hasQueuedMutations()) {
        // Defer refetch until all queued mutations complete
        setOnQueueEmpty(() => cache.refetch());
      } else {
        // No queued mutations, refetch immediately
        cache.refetch();
      }
    }
  });
};
